import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from '../../../../ui/toast';
import {
  ChevronDownIcons,
  CircleCheckIcons,
  UsersIcons,
} from '../../../../icons/icons';
import { MoveDropdown } from '../../../../ui/MoveDropdown/MoveDropdown';
import { editJobApplicant } from '../../../../utils/api_service/JobOpenings';
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';
import { RootState } from '../../../../utils/redux_store/store';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';

type ScatterModalProps = {
  segmentedCandidates: Applicant[];
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  setTabTitles: (titles: string[]) => void | undefined;
  setActiveTab: (value: number) => void;
  tabTitles: string[];
  useSkillPercentage?: boolean;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string
  ) => void;
  currentTab?: string;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  filteredSkills?: string[];
};

export const ScatterModal = ({
  segmentedCandidates,
  selectedCandidates,
  setSelectedCandidates,
  setTabTitles,
  setActiveTab,
  tabTitles,
  useSkillPercentage,
  onCandidateClick,
  currentTab,
  isApplicantSelected,
  filteredSkills,
}: ScatterModalProps) => {
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);

  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = segmentedCandidates.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(segmentedCandidates.length / itemsPerPage);
  const maxButtons = 5;

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  const options = [
    'Outstanding',
    'Auto-Assessment',
    'Shortlisted',
    'Unsuccessful',
    'Suitable',
    'New',
    ...(jobOpeningElements?.includes('Psychometric')
        ? ['Psychometric']
        : []),
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
  ];

  // Map display values to API values
  const mapStatusToAPI = (displayStatus: string): string => {
    const statusMap: Record<string, string> = {
      Outstanding: 'Outstanding',
      'Auto-Assessment': 'Auto-Assessment',
      Shortlisted: 'Shortlisted',
      Unsuccessful: 'Unsuccessful-Pending', // Changed to pending status
      Suitable: 'Suitable',
      New: 'New',
    };
    return statusMap[displayStatus] || displayStatus;
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const { startPage, endPage } = useMemo(() => {
    let startPage = currentPage - Math.floor(maxButtons / 2);
    let endPage = currentPage + Math.floor(maxButtons / 2);

    if (startPage <= 0) {
      startPage = 1;
      endPage = Math.min(maxButtons, totalPages);
    }

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, totalPages - maxButtons + 1);
    }

    return { startPage, endPage };
  }, [currentPage, totalPages, maxButtons]);

  const openTab = (name: string, userId: string) => {
    const candidate = segmentedCandidates.find(
      (item) => item.userId === userId,
    );

    // Use the new onCandidateClick prop if available, otherwise use the old tab system
    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    // Fallback to old tab system
    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === userId,
    );
    if (!nameExists) {
      setTabTitles([...tabTitles, name]);
      const selectedItem: Applicant | undefined = segmentedCandidates.find(
        (item) => item.userId === userId,
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        { tabId: tabTitles.length, candidate: selectedItem },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + name + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const handleDropdownToggle = (candidateId: string) => {
    setOpenDropdown((prev) => (prev === candidateId ? null : candidateId));
  };

  const handleOptionClick = async (
    candidateId: string | string[],
    option: string,
  ) => {
    const ids = Array.isArray(candidateId) ? candidateId : [candidateId];

    // Make sure we have a job opening ID
    if (!id) {
      toast.error('Job opening ID not found', {
        position: 'bottom-center',
      });
      return;
    }

    try {
      // Use the job opening ID from URL params for the API call
      await Promise.all(
        ids.map((applicantId) =>
          // First param is job opening ID, second is applicant ID
          editJobApplicant(id, applicantId, {
            applicantStatus: mapStatusToAPI(option),
          }),
        ),
      );

      toast.success(
        `Successfully moved ${ids.length} candidate(s) to ${
          option === 'Unsuccessful' ? 'Unsuccessful (Pending)' : option
        }`,
        {
          position: 'bottom-center',
        },
      );
      setSelectedItems([]);
    } catch (error) {
      toast.error('Error changing applicant status', {
        position: 'bottom-center',
      });
    }
    setOpenDropdown(null);
    setBulkActionOpen(false);
  };

  const handleSelectAll = (type: 'all' | 'page') => {
    const currentPageIds = new Set(currentItems.map((item) => item.userId));
    const allIds = new Set(segmentedCandidates.map((item) => item.userId));
    const selectedSet = new Set(selectedItems);

    if (type === 'all') {
      // If all items are already selected, clear selection
      if (selectedItems.length === segmentedCandidates.length) {
        setSelectedItems([]);
      } else {
        // Select all items regardless of current selection
        setSelectedItems(Array.from(allIds));
      }
    } else {
      // If all current page items are selected, clear selection
      const isCurrentPageFullySelected = Array.from(currentPageIds).every(
        (id) => selectedSet.has(id),
      );
      if (isCurrentPageFullySelected) {
        setSelectedItems([]);
      } else {
        // Keep other selected items and add current page items
        const newSelection = new Set(selectedItems);
        currentPageIds.forEach((id) => newSelection.add(id));
        setSelectedItems(Array.from(newSelection));
      }
    }
    setSelectionDropdownOpen(false);
  };

  const [selectionDropdownOpen, setSelectionDropdownOpen] = useState(false);
  const selectDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectionDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        selectDropdownRef.current &&
        !selectDropdownRef.current.contains(e.target as Node)
      ) {
        setSelectionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [selectionDropdownOpen]);

  const toggleSelectItem = (userId: string) => {
    setSelectedItems((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  return (
    <div className='bg-slate-50/50 rounded-2xl p-1 min-h-[30vh]'>
      { /* Header with bulk actions */ }
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-3'>
          <div className='relative' ref={selectDropdownRef}>
            <button
                type='button'
                onClick={() => setSelectionDropdownOpen(!selectionDropdownOpen)}
                className='flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white rounded-xl border border-slate-200/90 hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500/20'
            >
              <input
                  type='checkbox'
                  className='form-checkbox h-3.5 w-3.5 text-blue-600 rounded border-slate-300 transition duration-150 ease-in-out'
                  checked={selectedItems.length > 0}
                  onChange={() => {}}
              />
              <span>Select</span>
              <ChevronDownIcons
                  className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${
                    selectionDropdownOpen ? 'rotate-180 text-slate-600' : ''
                  }`}
              />
            </button>

            { selectionDropdownOpen && (
              <div className='absolute z-50 mt-1.5 w-52 rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/80 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.04)] p-1.5 transition-all animate-in fade-in zoom-in-95'>
                <div className='px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 mb-1'>
                  Selection Options
                </div>
                <button
                    type='button'
                    onClick={() => handleSelectAll('page')}
                    className='flex items-center justify-between w-full px-2.5 py-2 text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100/90 rounded-xl transition-all'
                >
                  <span>Select Current Page</span>
                  <span className='text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-md'>
                    { currentItems.length }
                  </span>
                </button>
                <button
                    type='button'
                    onClick={() => handleSelectAll('all')}
                    className='flex items-center justify-between w-full px-2.5 py-2 text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100/90 rounded-xl transition-all'
                >
                  <span>Select All Candidates</span>
                  <span className='text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-md'>
                    { segmentedCandidates.length }
                  </span>
                </button>
              </div>
            ) }
          </div>

          { selectedItems.length > 0 && (
            <MoveDropdown
                triggerLabel={`Bulk Move (${selectedItems.length})`}
                triggerIcon={<UsersIcons className='h-3.5 w-3.5 text-white' />}
                triggerClassName='bg-blue-600 hover:bg-blue-700 text-white hover:text-white border-transparent shadow-sm'
                options={options}
                onSelect={(option) => handleOptionClick(selectedItems, option)}
                isOpen={bulkActionOpen}
                onToggle={() => setBulkActionOpen(!bulkActionOpen)}
            />
          ) }
        </div>

        <span className='inline-flex items-center px-2.5 py-1 rounded-full bg-white border border-slate-200/80 text-xs font-medium text-slate-600 shadow-xs'>
          { segmentedCandidates.length } candidate{ segmentedCandidates.length === 1 ? '' : 's' }
        </span>
      </div>

      { /* Candidates List */ }
      <ul className='space-y-3'>
        { currentItems.map((item) => (
          <li
              key={item.userId}
              className={`bg-white rounded-2xl p-4 transition-all duration-200 border ${
                selectedItems.includes(item.userId)
                  ? 'border-blue-500/80 ring-2 ring-blue-500/20 shadow-md'
                  : 'border-slate-200/70 hover:border-slate-300/80 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] shadow-xs'
              }`}
          >
            <div className='flex items-center gap-4'>
              <input
                  type='checkbox'
                  checked={selectedItems.includes(item.userId)}
                  onChange={() => toggleSelectItem(item.userId)}
                  className='form-checkbox h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500/30'
              />

              <div className='flex-shrink-0'>
                { (() => {
                  // Helper function to get initials
                  const getInitials = (name: string) => {
                    const parts = name.split(' ').filter(Boolean);
                    return parts.length > 1
                      ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
                      : parts[0][0].toUpperCase();
                  };

                  // Handle unsuccessful candidates based on status and selection (works across all tabs)
                  const isSelected = isApplicantSelected && isApplicantSelected(item);
                  const applicantStatus = item.applicantStatus || '';

                  if (applicantStatus === 'Unsuccessful-Closed') {
                    // Always show initials in RED for closed unsuccessful candidates
                    return (
                      <div className='w-12 h-12 bg-red-600 text-white rounded-2xl flex items-center justify-center text-sm font-bold shadow-xs'>
                        { getInitials(item.name) }
                      </div>
                    );
                  } else if (applicantStatus === 'Unsuccessful-Pending' && isSelected) {
                    // Show initials in YELLOW when selected
                    return (
                      <div className='w-12 h-12 bg-amber-500 text-white rounded-2xl flex items-center justify-center text-sm font-bold shadow-xs'>
                        { getInitials(item.name) }
                      </div>
                    );
                  } else if (applicantStatus === 'Unsuccessful-Pending' && !isSelected) {
                    // Show headshot if available for unselected pending
                    return item.symbol === 'circle' ? (
                      <div className='w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200/80 text-slate-700 rounded-2xl flex items-center justify-center text-sm font-bold shadow-xs border border-slate-200/60'>
                        { getInitials(item.name) }
                      </div>
                    ) : (
                      <img
                          src={item.symbol.replace('image://', '')}
                          alt='Profile'
                          className='w-12 h-12 rounded-2xl object-cover shadow-xs border border-slate-200/60'
                      />
                    );
                  }

                  // Default logic for other tabs
                  return item.symbol === 'circle' ? (
                    <div className='w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-100/70 text-blue-700 rounded-2xl flex items-center justify-center text-sm font-bold shadow-xs border border-blue-200/60'>
                      { getInitials(item.name) }
                    </div>
                  ) : (
                    <img
                        src={item.symbol.replace('image://', '')}
                        alt='Profile'
                        className='w-12 h-12 rounded-2xl object-cover shadow-xs border border-slate-200/60'
                    />
                  );
                })() }
              </div>

              <div
                  className='flex-grow cursor-pointer min-w-0'
                  onClick={() =>
                    openTab(`${item.firstName} ${item.lastName}`, item.userId)
                  }
              >
                <h3 className='text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors truncate'>
                  { `${item.firstName} ${item.lastName}` }
                </h3>
                <div className='mt-1 flex flex-col gap-1.5'>
                  <div className='flex flex-wrap items-center gap-2 text-xs text-slate-500'>
                    <span className='inline-flex items-center gap-1 bg-slate-50 border border-slate-200/60 px-2 py-0.5 rounded-lg text-slate-600'>
                      <span className='text-slate-400'>Experience:</span>
                      <span className='font-semibold text-slate-800'>
                        { Number(item.value[1]).toFixed(1) } yrs
                      </span>
                    </span>
                    <span className='inline-flex items-center gap-1 bg-slate-50 border border-slate-200/60 px-2 py-0.5 rounded-lg text-slate-600'>
                      <span className='text-slate-400'>Skills:</span>
                      <span className='font-semibold text-slate-800'>
                        { useSkillPercentage
                          ? `${Number(item.value[0]).toFixed(2)}%`
                          : `${Number(item.value[0]).toFixed(0)}` }
                      </span>
                    </span>
                  </div>
                  <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500'>
                    <p className='flex items-center gap-1'>
                      <span className='text-slate-400'>Attributes:</span>
                      <span className='font-medium text-slate-700'>
                        { item.matchedBehavior?.join(', ') ?? 'N/A' }
                      </span>
                    </p>
                    <p className='flex items-center gap-1'>
                      <span className='text-slate-400'>Keywords:</span>
                      <span className='font-medium text-slate-700'>
                        { item.keywords?.join(', ') ?? 'N/A' }
                      </span>
                    </p>
                  </div>
                  <p className='text-xs text-slate-500 flex items-center gap-1 truncate'>
                    <span className='text-slate-400 shrink-0'>Matched Skills:</span>
                    <span className='font-medium text-slate-700 truncate'>
                      { (() => {
                        const skills = item.matchedSkills || [];
                        const displayedSkills =
                          filteredSkills && filteredSkills.length > 0
                            ? skills.filter((skill) =>
                              filteredSkills.some(
                                (fs) => fs.toLowerCase() === skill.toLowerCase(),
                              ),
                            )
                            : skills;

                        return displayedSkills.length > 0
                          ? displayedSkills.join(', ')
                          : 'N/A';
                      })() }
                    </span>
                  </p>
                </div>
              </div>

              <div className='flex-shrink-0'>
                <MoveDropdown
                    options={options}
                    onSelect={(option) => handleOptionClick(item.userId, option)}
                    isOpen={openDropdown === item.userId}
                    onToggle={() => handleDropdownToggle(item.userId)}
                />
              </div>
            </div>
          </li>
        )) }
      </ul>

      { /* Pagination */ }
      { totalPages > 1 && (
        <div className='flex justify-center items-center gap-2 mt-6'>
          <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className='px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 bg-white border border-slate-200/90 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all'
          >
            Previous
          </button>

          { [...Array(endPage - startPage + 1)].map((_, index) => (
            <button
                key={index}
                onClick={() => paginate(startPage + index)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  currentPage === startPage + index
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200/90'
                }`}
            >
              { startPage + index }
            </button>
          )) }

          <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className='px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 bg-white border border-slate-200/90 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all'
          >
            Next
          </button>
        </div>
      ) }
    </div>
  );
};
